import {
  FederatedSearchRecipientsParams,
  FederatedSearchRecipientsResults,
  TenantMemberDisplayData,
} from '@kudos/http-client'
import { useQuery } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { useEffect, useMemo, useRef } from 'react'
import { useDebounce } from 'use-debounce'

export type SearchResults = {
  tenantMembers: TenantMemberDisplayData[]
}

export type TenantMemberSearchFn = (params: { search?: string }) => Promise<SearchResults>

const useSearchTenantMembersAndTeams = (
  searchText: string,
  selectedMembersIds: string[] = [],
  searchFn: (params: FederatedSearchRecipientsParams) => Promise<FederatedSearchRecipientsResults>,
) => {
  const [search, setSearch] = useDebounce(searchText, 250)
  const lastMatches = useRef<FederatedSearchRecipientsResults | undefined>()

  const { data: matches, isSuccess } = useQuery({
    queryKey: ['searchTenantMembersAndTeams', search],
    queryFn: async () => {
      const result = await searchFn({ search })
      return result
    },
    placeholderData: lastMatches.current,
    enabled: !isEmpty(search),
  })

  if (isSuccess) {
    lastMatches.current = matches
  }

  const searchedTenantMembersAndTeams = useMemo(() => {
    if (isEmpty(search) || !matches) {
      return []
    }

    const teamMatches =
      matches.teams?.filter(match => !selectedMembersIds.find(id => id === match.id)) || []
    const memberMatches =
      matches.tenantMembers?.filter(match => !selectedMembersIds.find(id => id === match.id)) || []
    const allMatches = [...teamMatches, ...memberMatches]
    return allMatches
  }, [matches, search])

  useEffect(() => {
    setSearch(searchText)
  }, [searchText])

  return {
    searchedTenantMembersAndTeams,
  }
}

export default useSearchTenantMembersAndTeams
